import * as React from 'react';
import { createContext, useCallback, useContext, useRef, useState } from 'react';

/**
 * Context for programmatic position focus in the List of Services assistance page.
 *
 * ## How it works
 *
 * 1. **Requesting focus** — The articles column calls `requestFocus(positionIndex)` when the
 *    user clicks the crosshair/Focus button. This stores the target position index in a ref
 *    and bumps a counter to trigger re-renders in the positions column.
 *
 * 2. **Sub-chapter expansion** — `ListOfServicesChapter` reads `pendingPositionIndex` during
 *    render (before effects run). If a sub-chapter contains the target position, it force-expands
 *    that sub-chapter via local state.
 *
 * 3. **Position card expansion + scroll** — `ListOfServicesPositionContainer` runs a `useEffect`
 *    keyed on `focusTrigger`. When the effect finds that `pendingPositionIndex` matches its own
 *    position index, it expands the card, resets `manuallyCollapsed`, clears the pending value
 *    via `consumeFocus()`, and scrolls the card into view.
 *
 * The ref + trigger pattern is used because a ref alone doesn't cause re-renders, and a state
 * value alone would be consumed synchronously during render by the first matching component.
 *
 * The context also carries the mirror direction: `focusArticles` (positions column → articles
 * column) delegates to the page's `scrollArticlesToPosition`, and the persisted auto-scroll
 * flag that gates the automatic left→right follow in `useScrollSync`.
 */

interface FocusPositionContextValue {
    /** The position index currently pending focus, or null. Read during render or in effects. */
    pendingPositionIndex: number | null;
    /** Incrementing counter — use as a useEffect dependency to re-evaluate on each focus request. */
    focusTrigger: number;
    /** Request focus on a position. Call from the articles column Focus button handler. */
    requestFocus: (positionIndex: number) => void;
    /** Mark the pending focus as consumed. Call after the position card has expanded + scrolled. */
    consumeFocus: () => void;
    /** Scroll the articles (right) column so the position group lands at the top. Always enabled,
     *  independent of the auto-scroll toggle. Call from the positions column Focus button handler. */
    focusArticles: (positionIndex: number) => void;
    /** Temporarily disable the left→right auto-follow in useScrollSync. Call right before
     *  programmatically scrolling the positions column, so the auto-follow doesn't mistake
     *  that scroll for user scrolling and move the articles column. */
    suppressAutoSync: (ms?: number) => void;
    /** Whether the automatic left→right scroll-following is enabled (persisted per user). */
    autoScrollEnabled: boolean;
    setAutoScrollEnabled: (enabled: boolean) => void;
}

const FocusPositionContext = createContext<FocusPositionContextValue | null>(null);

export const useFocusPosition = (): FocusPositionContextValue => {
    const ctx = useContext(FocusPositionContext);
    if (!ctx) {
        throw new Error('useFocusPosition must be used within a FocusPositionProvider');
    }
    return ctx;
};

interface FocusPositionProviderProps {
    children: React.ReactNode;
    focusArticles: (positionIndex: number) => void;
    suppressAutoSync: (ms?: number) => void;
    autoScrollEnabled: boolean;
    setAutoScrollEnabled: (enabled: boolean) => void;
}

export const FocusPositionProvider = ({
    children,
    focusArticles,
    suppressAutoSync,
    autoScrollEnabled,
    setAutoScrollEnabled,
}: FocusPositionProviderProps) => {
    const pendingRef = useRef<number | null>(null);
    const [focusTrigger, setFocusTrigger] = useState(0);

    const requestFocus = useCallback((positionIndex: number) => {
        pendingRef.current = positionIndex;
        setFocusTrigger((c) => c + 1);
    }, []);

    const consumeFocus = useCallback(() => {
        pendingRef.current = null;
    }, []);

    // Build a stable-shape context value. The trigger counter changes on each focus request,
    // which is intentional — consumers that depend on it will re-render.
    const value: FocusPositionContextValue = {
        pendingPositionIndex: pendingRef.current,
        focusTrigger,
        requestFocus,
        consumeFocus,
        focusArticles,
        suppressAutoSync,
        autoScrollEnabled,
        setAutoScrollEnabled,
    };

    return <FocusPositionContext.Provider value={value}>{children}</FocusPositionContext.Provider>;
};
